// @flow
import type {Node} from 'react';

import {useMountEffect} from '@react-hookz/web';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

import errorImage from 'assets/images/error.svg';
import flightBookingImage from 'assets/images/flight-booking.svg';
import {PaypalOverlay} from 'components/PaypalOverlay';
import {
  PAYPAL_CANCEL,
  PAYPAL_PARAM,
  PAYPAL_PENDING,
  PAYPAL_SUCCESS,
  PAYPAL_MISSING_AUTHORIZATION
} from 'consts';
import {setBookingInProcess} from 'redux/uiState/layout/booking/actions';
import {getJourneyId, getToken} from 'redux/uiState/layout/booking/reducer';
import {setSelected} from 'redux/uiState/payment/actions';
import {getErrors} from 'redux/v1/book/reducer';
import {getJourney} from 'redux/v1/journey/reducer';
import {deletePayment} from 'redux/v1/paymentTravel/delete/actions';
import {deletePaypalPayment} from 'redux/v1/paymentTravel/deletePaypal/actions';
import {
  getPaypalRedirectUrl,
  isLoaded,
} from 'redux/v1/paymentTravel/paypalVault/reducer';


export const PaypalLayer = (): Node => {
  const [open, setOpen] = useState(false);
  const [overlayType, setOverlayType] = useState('error');
  const {t} = useTranslation(['general']);
  const dispatch = useDispatch();
  const journeyId = useSelector(getJourneyId);
  const token = useSelector(getToken);
  const {vacancyTime, offersUrl} = useSelector(getJourney);
  const expiredTime =
    new Date((vacancyTime + 3600) * 1000).getTime() - Date.now();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isRedirectUrlLoaded = useSelector(isLoaded);
  const paypalRedirectUrl = useSelector(getPaypalRedirectUrl);
  const bookingErrors = useSelector(getErrors)?.response?.data;

  const paypalRedirect = queryParams.get(PAYPAL_PARAM);

  const windowUrl = new URL(window.location.href);

  const handleClose = async () => {
    if (expiredTime <= 0) {
      window.location.href = offersUrl;
    } else {
      setOpen(false);
      await dispatch(deletePayment(journeyId, token));
      dispatch(setBookingInProcess(false));
      dispatch(setSelected(''));
      if (paypalRedirect) {
        windowUrl.searchParams.delete(PAYPAL_PARAM);
        location.search = windowUrl.search;
        window.history.pushState(null, '', windowUrl.toString());
      }
    }
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted && windowUrl.searchParams.get(PAYPAL_PARAM) === PAYPAL_PENDING) {
        window.location.reload();
      }
  });

  useEffect(() => {
    if (
      paypalRedirect === PAYPAL_PENDING &&
      isRedirectUrlLoaded &&
      !paypalRedirectUrl
    ) {
      setOpen(true);
    }
  }, [isRedirectUrlLoaded]);

  useEffect(() => {
    if (
      paypalRedirect === PAYPAL_SUCCESS &&
      expiredTime >= 0 &&
      bookingErrors?.message === PAYPAL_MISSING_AUTHORIZATION
    ) {
      dispatch(deletePaypalPayment(journeyId, token));
      setOpen(true);
    }
  }, [bookingErrors]);

  useMountEffect(() => {
    if (expiredTime <= 0) {
      setOverlayType('expired');
    }

    if (paypalRedirect === PAYPAL_PENDING && !paypalRedirectUrl) {
      setOpen(true);
    } else if (
      paypalRedirect === PAYPAL_SUCCESS &&
      !paypalRedirectUrl &&
      expiredTime <= 0
    ) {
      dispatch(deletePaypalPayment(journeyId, token));
      setOpen(true);
    } else if (paypalRedirect === PAYPAL_CANCEL) {
      setOpen(true);
    }
  }, [expiredTime, token, journeyId, dispatch, paypalRedirect]);

  if (!open) {
    return null;
  }

  return (
    <PaypalOverlay
      buttonLabel={t(`paypal.${overlayType}Overlay.buttonLabel`)}
      content={t(`paypal.${overlayType}Overlay.content`)}
      imgSrc={overlayType === 'error' ? errorImage : flightBookingImage}
      onClose={handleClose}
      open={open}
      subTitle={t(`paypal.${overlayType}Overlay.subTitle`)}
      title={t(`paypal.${overlayType}Overlay.title`)}
    />
  );
};
